import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Box, Chip, CircularProgress, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import QrCodeIcon from '@mui/icons-material/QrCode';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import moment from 'moment';

import { getCouponsState } from '../../../features/coupon/get_coupons';
import { GET_ROUTE_COUPON_FORMS, MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_FORMAT, ROUTE_COUPON_FORMS_NEW } from '../../../core/constants';
import { DeleteButton, DuplicateButton } from '../../common/button';
import { updateCouponStatusAction } from '../../../features/coupon/update_coupon_status';
import { getCouponQRCodeNotificationData, updateCouponStatusNotificationData } from '../../../core/utils/notification_utils';
import useNotification from '../../../core/hooks/use_notification';
import { ApiRequestStatusEnum } from '../../../core/enums/common/api';
import { CouponActivationTypeEnum, CouponStatusEnum } from '../../../core/enums/price/coupon';
import { FormRouteEnum } from '../../../core/enums/common/route';
import { getButtonText, getCouponStatus, getUpdateCouponStatus } from '../../common/utils/coupon_utils';
import { getCouponQRCodeAction } from '../../../features/coupon/get_coupon_qr_code';

const CouponsList = ({ refreshCouponList, tab }) => {
    const { showErrorMessage, showNotification } = useNotification();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const getCoupons = useSelector(getCouponsState);
    const updateOptions = tab === 0;

    const [updatingStatuses, setUpdatingStatuses] = useState({});
    const [downloadingQRs, setDownloadingQRs] = useState({});

    const onUpdateCouponStatus = (coupon, status = null) => {
        setUpdatingStatuses(prev => ({ ...prev, [coupon?.couponId]: true }));
        const couponStatus = status || getUpdateCouponStatus(coupon?.couponStatus);

        dispatch(updateCouponStatusAction({ couponId: coupon?.couponId, status: couponStatus }))
            .then(response => {
                showNotification(updateCouponStatusNotificationData(response, couponStatus));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshCouponList();
            })
            .finally(() => {
                setUpdatingStatuses(prev => ({ ...prev, [coupon?.couponId]: false }));
            });
    };

    const onGetCouponQRCode = (id) => {
        setDownloadingQRs(prev => ({ ...prev, [id]: true }));
        dispatch(getCouponQRCodeAction(id))
            .then(response => showErrorMessage(getCouponQRCodeNotificationData(response)))
            .finally(() => {
                setDownloadingQRs(prev => ({ ...prev, [id]: false }));
            });
    };

    const getStatusChipColor = (status) => {
        switch (status) {
            case CouponStatusEnum.ACTIVE:
            case 'ACTIVE':
                return 'success';
            case CouponStatusEnum.DRAFT:
            case 'DRAFT':
                return 'warning';
            case CouponStatusEnum.INACTIVE:
            case 'INACTIVE':
            default:
                return 'default';
        }
    };

    return (
        <TableContainer component={ Paper } variant="outlined">
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                        <TableCell>Nazwa robocza</TableCell>
                        <TableCell align="center">Data obowiązywania</TableCell>
                        <TableCell align="center">Wszystkie pody</TableCell>
                        <TableCell align="center">Wszystkie huby</TableCell>
                        <TableCell align="center">Wszyscy klienci</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell align="right" sx={ { pr: 3 } }></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {
                        getCoupons?.data?.content?.map(coupon => {
                            const isStatusUpdating = updatingStatuses[coupon?.couponId] || false;
                            const isQrDownloading = downloadingQRs[coupon?.couponId] || false;
                            const isActive = coupon?.couponStatus === CouponStatusEnum.ACTIVE || coupon?.couponStatus === 'ACTIVE';
                            const isDraft = coupon?.couponStatus === CouponStatusEnum.DRAFT || coupon?.couponStatus === 'DRAFT';

                            return (
                                <TableRow key={ coupon?.couponId } hover>
                                    <TableCell sx={ { fontWeight: 500 } }>{ coupon?.workingName }</TableCell>
                                    <TableCell align="center">
                                        <Box sx={ { display: 'flex', flexDirection: 'column', alignItems: 'center' } }>
                                            <Box sx={ { variant: 'body2' } }>
                                                Od:&nbsp;{ moment(coupon?.timeFrom, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                                            </Box>
                                            <Box sx={ { variant: 'body2', color: 'text.secondary' } }>
                                                Do:&nbsp;{ coupon?.withoutEndDate ? 'Brak daty końcowej' : moment(coupon?.timeTo, MISC_DATE_ISO_FORMAT).format(MISC_DATE_POLISH_FORMAT) }
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={ coupon?.allPods ? "TAK" : "NIE" }
                                            color={ coupon?.allPods ? "success" : "default" }
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={ coupon?.allHubs ? "TAK" : "NIE" }
                                            color={ coupon?.allHubs ? "success" : "default" }
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={ coupon?.allCustomers ? "TAK" : "NIE" }
                                            color={ coupon?.allCustomers ? "success" : "default" }
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            label={ getCouponStatus(coupon?.couponStatus) }
                                            color={ getStatusChipColor(coupon?.couponStatus) }
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right" sx={ { pr: 2 } }>
                                        <Stack direction="row" spacing={ 1 } justifyContent="flex-end"
                                               alignItems="center">
                                            {
                                                updateOptions && (
                                                    <Tooltip title={ getButtonText(coupon?.couponStatus) }>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color={ isActive ? 'error' : 'success' }
                                                                onClick={ () => onUpdateCouponStatus(coupon) }
                                                                disabled={ isDraft || isStatusUpdating }
                                                            >
                                                                { isStatusUpdating ?
                                                                    <CircularProgress size={ 20 } color="inherit"/> :
                                                                    <PowerSettingsNewIcon fontSize="small"/> }
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )
                                            }
                                            {
                                                coupon?.couponActivationType === CouponActivationTypeEnum.FLYER_QR.value && (
                                                    <Tooltip title="Pobierz kod QR">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                onClick={ () => onGetCouponQRCode(coupon?.couponId) }
                                                                disabled={ isQrDownloading }
                                                            >
                                                                { isQrDownloading ?
                                                                    <CircularProgress size={ 20 } color="inherit"/> :
                                                                    <QrCodeIcon fontSize="small"/> }
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )
                                            }
                                            <DuplicateButton size="small"
                                                             onClick={ () => navigate(ROUTE_COUPON_FORMS_NEW, {
                                                                 state: {
                                                                     action: FormRouteEnum.NEW,
                                                                     couponIdToDuplicate: coupon?.couponId
                                                                 }
                                                             }) }/>
                                            {
                                                updateOptions
                                                    ? (
                                                        <Tooltip title="Edytuj">
                                                            <IconButton size="small" color="primary"
                                                                        onClick={ () => navigate(GET_ROUTE_COUPON_FORMS(coupon?.couponId), { state: { action: FormRouteEnum.EDIT } }) }>
                                                                <EditIcon fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    )
                                                    : (
                                                        <Tooltip title="Podgląd">
                                                            <IconButton size="small"
                                                                        onClick={ () => navigate(GET_ROUTE_COUPON_FORMS(coupon?.couponId), { state: { action: FormRouteEnum.VIEW } }) }>
                                                                <SearchIcon fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    )
                                            }
                                            {
                                                updateOptions &&
                                                <DeleteButton size="small"
                                                              onDeleteClick={ () => onUpdateCouponStatus(coupon, CouponStatusEnum.DELETED) }
                                                              isLoading={ isStatusUpdating }/>
                                            }
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })
                    }
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default CouponsList;